export type ApplySuggestionHandler = (raw: string) => Promise<boolean> | boolean
export interface FieldApplyEntry {
  handler?: ApplySuggestionHandler
  peek?: () => unknown
}

const fieldEntries = new Map<string, FieldApplyEntry>() // fieldKey -> entry with handler/peek
const fieldEntryListeners = new Set<() => void>()
const fieldValueListeners = new Map<string, Set<() => void>>()

export type SuggestionApplyEvent =
  | {
      phase: "start"
      source: "single" | "all"
      requestedKeys: string[]
      applyTargetPath?: string
    }
  | {
      phase: "complete"
      source: "single" | "all"
      requestedKeys: string[]
      appliedKeys: string[]
      applyTargetPath?: string
    }

const suggestionApplyListeners = new Set<
  (event: SuggestionApplyEvent) => void
>()

function notifyFieldEntriesChanged(keys: string[]) {
  fieldEntryListeners.forEach((listener) => listener())
  keys.forEach(notifyFieldApplyValueChanged)
}

export function subscribeFieldApplyEntries(listener: () => void): () => void {
  fieldEntryListeners.add(listener)
  return () => fieldEntryListeners.delete(listener)
}

export function subscribeFieldApplyValue(
  key: string,
  listener: () => void
): () => void {
  const listeners = fieldValueListeners.get(key) ?? new Set<() => void>()
  listeners.add(listener)
  fieldValueListeners.set(key, listeners)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) fieldValueListeners.delete(key)
  }
}

export function notifyFieldApplyValueChanged(key: string): void {
  fieldValueListeners.get(key)?.forEach((listener) => listener())
}

export function subscribeSuggestionApplyEvents(
  listener: (event: SuggestionApplyEvent) => void
): () => void {
  suggestionApplyListeners.add(listener)
  return () => suggestionApplyListeners.delete(listener)
}

export function notifySuggestionApplyEvent(event: SuggestionApplyEvent): void {
  suggestionApplyListeners.forEach((listener) => listener(event))
}

export function isSuccessfulApplyAllEvent(
  event: SuggestionApplyEvent
): event is Extract<SuggestionApplyEvent, { phase: "complete" }> {
  return (
    event.phase === "complete" &&
    event.source === "all" &&
    event.requestedKeys.length > 0 &&
    event.requestedKeys.every((key) => event.appliedKeys.includes(key))
  )
}

export function registerFieldApplyEntries(
  entries: Record<string, ApplySuggestionHandler | FieldApplyEntry>
): () => void {
  const keys = Object.keys(entries)
  keys.forEach((key) => {
    const v = entries[key]
    if (typeof v === "function") {
      fieldEntries.set(key, { handler: v })
    } else {
      fieldEntries.set(key, { handler: v.handler, peek: v.peek })
    }
  })
  notifyFieldEntriesChanged(keys)

  return () => {
    keys.forEach((key) => {
      fieldEntries.delete(key)
    })
    notifyFieldEntriesChanged(keys)
  }
}

export function isFieldApplyAvailable(key: string): boolean {
  if (!key) return false
  const entry = fieldEntries.get(key)
  return Boolean(entry && entry.handler)
}

export function isApplySuggestionTargetCurrent(targetPath?: string): boolean {
  return !!targetPath && isSamePath(targetPath, window.location.pathname)
}

const isSamePath = (left: string, right: string) =>
  left.replace(/\/+$/, "") === right.replace(/\/+$/, "")

export function isSuggestionApplyEventForCurrentPath(
  event: SuggestionApplyEvent,
  currentPath = window.location.pathname
): boolean {
  return (
    !event.applyTargetPath || isSamePath(event.applyTargetPath, currentPath)
  )
}

export async function applyValueToField(
  key: string,
  raw: string
): Promise<boolean> {
  const entry = fieldEntries.get(key)
  if (!entry || !entry.handler) return false
  try {
    const ok = await entry.handler(raw)
    return Boolean(ok)
  } catch {
    return false
  }
}

export function parseApplyLanguageClass(className?: string): string | null {
  if (!className) return null
  // Expecting class like: "language-apply:basicForm.systemPrompt"
  const m = /^language-apply:(.+)$/.exec(className)
  if (m && m[1]) {
    return m[1]
  }
  return null
}

export function getFieldCurrentValue(key: string): unknown {
  const entry = fieldEntries.get(key)
  if (!entry || !entry.peek) return undefined
  try {
    return entry.peek()
  } catch {
    return undefined
  }
}

export function isFieldEmpty(key: string): boolean {
  const value = getFieldCurrentValue(key)
  if (value == null) return true
  if (typeof value === "string") return value.trim().length === 0
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === "object")
    return Object.keys(value as object).length === 0
  return false
}
